<template>
  <div
    data-test="task-dashboard-skeleton"
    :data-view="view"
    class="flex flex-col gap-4"
    aria-busy="true"
    :aria-label="$t('global.loading')"
  >
    <template v-if="withHeader">
      <div
        data-test="skeleton-header"
        aria-hidden="true"
        class="flex flex-col gap-2 sm:flex-row sm:items-center"
      >
        <BaseSkeletonBar height="h-8" width="w-56" class="dark:bg-slate-600" />
        <div class="flex flex-1 flex-wrap gap-2 sm:justify-end">
          <BaseSkeletonBar
            height="h-[38px]"
            width="w-64"
            class="dark:bg-slate-600"
          />
          <BaseSkeletonBar
            height="h-[38px]"
            width="w-48"
            class="dark:bg-slate-600"
          />
          <BaseSkeletonBar
            height="h-[38px]"
            width="w-10"
            class="dark:bg-slate-600"
          />
          <BaseSkeletonBar
            height="h-[38px]"
            width="w-28"
            class="dark:bg-slate-600"
          />
        </div>
      </div>
      <div aria-hidden="true" class="flex flex-wrap gap-2">
        <BaseSkeletonBar height="h-5" width="w-40" class="dark:bg-slate-600" />
        <BaseSkeletonBar height="h-5" width="w-56" class="dark:bg-slate-600" />
      </div>
      <div aria-hidden="true" class="flex flex-wrap gap-2">
        <BaseSkeleton :count="5">
          <BaseSkeletonBar
            height="h-8"
            width="w-36"
            class="rounded-lg dark:bg-slate-600"
          />
        </BaseSkeleton>
      </div>
    </template>
    <template v-if="view === 'board'">
      <div aria-hidden="true" class="flex flex-row gap-x-4">
        <BaseSkeleton :count="columnCount">
          <div
            data-test="skeleton-column"
            class="flex min-w-0 flex-1 flex-col gap-2"
          >
            <BaseSkeletonBar height="h-8" class="dark:bg-slate-600" />
            <BaseSkeleton :count="3">
              <TaskCardSkeleton />
            </BaseSkeleton>
          </div>
        </BaseSkeleton>
      </div>
    </template>
    <template v-else-if="view === 'list'">
      <div aria-hidden="true" class="flex flex-col gap-2">
        <BaseSkeletonBar height="h-9" class="dark:bg-slate-600" />
        <BaseSkeleton :count="8">
          <BaseSkeletonBar
            data-test="skeleton-row"
            height="h-8"
            class="dark:bg-slate-600"
          />
        </BaseSkeleton>
      </div>
    </template>
    <template v-else>
      <BaseSkeleton :count="2">
        <div aria-hidden="true" class="flex flex-col gap-2">
          <BaseSkeletonBar
            data-test="skeleton-group-header"
            height="h-8"
            class="dark:bg-slate-600"
          />
          <div class="grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-3">
            <BaseSkeleton :count="6">
              <TaskCardSkeleton />
            </BaseSkeleton>
          </div>
        </div>
      </BaseSkeleton>
    </template>
  </div>
</template>

<script>
import BaseSkeleton from '@c/BaseSkeleton.vue'
import BaseSkeletonBar from '@c/BaseSkeletonBar.vue'
import TaskCardSkeleton from './TaskCardSkeleton.vue'

/**
 * The task dashboard body as it looks before its tasks arrive, shaped like the selected
 * view (cards grid, status board or list), with the page header and chips line on top
 * while the board configuration itself is loading.
 */
export default {
  name: 'TaskDashboardSkeleton',
  components: { BaseSkeleton, BaseSkeletonBar, TaskCardSkeleton },
  props: {
    view: {
      type: String,
      required: false,
      default: 'cards',
      validator: (view) => ['cards', 'board', 'list'].includes(view)
    },
    /** Also draws the header controls and the chips line (board configuration still loading). */
    withHeader: { type: Boolean, required: false, default: false },
    /** Number of status columns of the board view. */
    columnCount: { type: Number, required: false, default: 4 }
  }
}
</script>
